import './IconButton.scss';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Tooltip } from '../Tooltip';
import type { TooltipConfig } from '../hooks/useTooltip';

interface IconButtonProps {
  onClick: () => void;
  icon: ReactNode;
  /** Alternate icon shown when selected (switch type only) */
  iconSelected?: ReactNode;
  /** Optional label displayed below the icon */
  label?: string;
  size?: 'xs' | 'small' | 'medium' | 'large';
  rounded?: boolean;
  type?: 'switch' | 'default';
  selected?: boolean; // only for switch type
  disabled?: boolean;
  tooltip?: string | TooltipConfig;
  className?: string;
  /** Ref forwarding for button element */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

const IconButton = ({
  onClick,
  icon,
  iconSelected,
  label,
  size = 'medium',
  rounded = false,
  type = 'default',
  selected = false,
  disabled = false,
  tooltip,
  className = '',
  buttonRef,
}: IconButtonProps) => {
  const prefix = prefixClass('q2-icon-button');
  const isSwitch = type === 'switch';
  const [isSelected, setIsSelected] = useState(isSwitch && selected);

  useEffect(() => {
    setIsSelected(isSwitch && selected);
  }, [isSwitch, selected]);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    // Only toggle internal state if uncontrolled (no iconSelected means parent isn't swapping icons)
    if (isSwitch && !iconSelected) {
      setIsSelected(prev => !prev);
    }
    onClick();
  };

  // Determine which icon to show:
  // - If iconSelected provided, use selected prop directly (controlled mode)
  // - Otherwise use internal isSelected state (uncontrolled mode)
  const showSelectedIcon = iconSelected ? selected : isSelected;
  const currentIcon = isSwitch && showSelectedIcon && iconSelected ? iconSelected : icon;

  // Apply 'selected' class:
  // - Single icon switch: only when selected (CSS provides visual feedback)
  // - Dual icon switch: always (it's always in an active state, icon swap shows which one)
  const applySelectedClass = isSwitch && (iconSelected ? true : isSelected);

  const button = (
    <button
      ref={buttonRef}
      type="button"
      className={classNames(
        prefix(),
        size,
        { rounded, selected: applySelectedClass, switch: isSwitch, 'with-label': !!label },
        className
      )}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={isSwitch ? showSelectedIcon : undefined}
    >
      {currentIcon}
      {label && <span className={prefix('__label')}>{label}</span>}
    </button>
  );

  // If tooltip is provided, wrap with Tooltip component
  if (tooltip) {
    const tooltipConfig =
      typeof tooltip === 'string'
        ? { content: tooltip, position: 'top' as const, alignment: 'center' as const }
        : tooltip;

    return <Tooltip {...tooltipConfig}>{button}</Tooltip>;
  }

  return button;
};

export default IconButton;
